/**
 * The Configuration Studio's view of campusone-core-service's institution-config module.
 *
 * MIRRORED, NOT IMPORTED. These shapes are transcribed from
 * src/institution-config/domain/contracts in the core service. Importing across the two
 * repositories would couple this bundle's build to a backend checkout being present, and —
 * worse — would let a refactor over there silently change what this console renders. A
 * transcription drifts loudly instead: the runtime validators in src/api/studio.ts refuse a
 * response that no longer matches, and the fix is a deliberate edit here.
 */

/** The layers a configuration value can come from, least specific first. */
export type ConfigurationLayerScope =
  | 'PLATFORM_DEFAULT'
  | 'COUNTRY_DEFAULT'
  | 'INSTITUTION_TYPE_TEMPLATE'
  | 'SUBSYSTEM_TEMPLATE'
  | 'INSTITUTION_OVERRIDE'
  | 'CAMPUS_OVERRIDE';

/**
 * Least specific first — the resolver walks this order and lets each layer overwrite the
 * one before. Transcribed from LAYER_PRECEDENCE in configuration-layer-scope.enum.ts; the
 * provenance ladder draws its rungs in exactly this order so that "further down wins" is
 * true on screen the way it is true in the resolver.
 */
export const LAYER_PRECEDENCE: readonly ConfigurationLayerScope[] = [
  'PLATFORM_DEFAULT',
  'COUNTRY_DEFAULT',
  'INSTITUTION_TYPE_TEMPLATE',
  'SUBSYSTEM_TEMPLATE',
  'INSTITUTION_OVERRIDE',
  'CAMPUS_OVERRIDE',
];

/** The four scopes stored as shared platform layers; the two override scopes live on versions. */
export const PLATFORM_OWNED_SCOPES: readonly ConfigurationLayerScope[] = [
  'PLATFORM_DEFAULT',
  'COUNTRY_DEFAULT',
  'INSTITUTION_TYPE_TEMPLATE',
  'SUBSYSTEM_TEMPLATE',
];

/** The nine dotted keys. Kept as a union so a typo in a key literal fails the build. */
export type ConfigurationKey =
  | 'operatingModel.governanceModel'
  | 'operatingModel.academicModel'
  | 'operatingModel.calendarModel'
  | 'operatingModel.gradingModel'
  | 'operatingModel.gradingScale'
  | 'operatingModel.progressionModel'
  | 'operatingModel.certificationModel'
  | 'operatingModel.curriculumModel'
  | 'operatingModel.attendanceModel';

/** One key as the registry describes it: label, closed value set, campus default. */
export interface ConfigurationKeyDescriptor {
  key: ConfigurationKey;
  label: string;
  allowedValues: readonly string[];
  campusOverridableByDefault: boolean;
}

/** GET /keys — the registry plus the precedence chain stated as a sentence. */
export interface ConfigurationKeysResponse {
  precedence: string;
  keys: ConfigurationKeyDescriptor[];
}

/**
 * A value as a layer states it. `campusOverridable` undefined means "no opinion";
 * an explicit boolean opens or closes the key for campuses below this layer.
 */
export interface ConfigurationLayerValue {
  value: string;
  campusOverridable?: boolean;
  note?: string;
}

export type ConfigurationValueMap = Partial<Record<ConfigurationKey, ConfigurationLayerValue>>;

/** One shared platform-owned layer row. */
export interface ConfigurationLayer {
  id: string;
  scope: ConfigurationLayerScope;
  /** '*' for the platform floor, 'CM', 'SECONDARY_SCHOOL', 'SECONDARY_SCHOOL:ANGLOPHONE'. */
  scopeKey: string;
  label: string;
  values: ConfigurationValueMap;
  enabled: boolean;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Where a resolved value came from, in terms a person can act on. */
export interface ConfigurationValueSource {
  scope: ConfigurationLayerScope;
  scopeKey: string;
  layerId: string | null;
  label: string;
}

/** One resolved key with the answer to "why": the winner and every layer it beat. */
export interface ResolvedConfigurationValue {
  key: ConfigurationKey;
  value: string;
  source: ConfigurationValueSource;
  campusOverridable: boolean;
  campusOverridableSource: ConfigurationValueSource | null;
  /** Layers that also set this key but lost, least specific first. */
  overriddenLayers: ConfigurationValueSource[];
  note: string | null;
}

export interface ResolvedConfiguration {
  institutionId: string;
  campusId: string | null;
  versionId: string | null;
  versionNumber: number | null;
  /** The nine resolved dimensions as a flat object; `values` carries the same answers with provenance. */
  operatingModel: Record<string, string>;
  values: Partial<Record<ConfigurationKey, ResolvedConfigurationValue>>;
  /** ISO timestamp; a Date on the wire serialises to a string. */
  resolvedAt: string;
}

/** The version lifecycle, transcribed from configuration-version-state.enum.ts. */
export type ConfigurationVersionState =
  | 'DRAFT'
  | 'VALIDATION_FAILED'
  | 'READY_FOR_REVIEW'
  | 'APPROVED'
  | 'SCHEDULED'
  | 'ACTIVE'
  | 'SUPERSEDED';

/**
 * The moves the STUDIO may offer per state — the client-side mirror of the backend's
 * CONFIGURATION_VERSION_TRANSITIONS, reduced to the actions this console exposes. A button
 * absent from this table cannot be clicked, so the server's refusal message is a bug here,
 * not a UI. Reviewer send-back (READY_FOR_REVIEW → DRAFT) has no endpoint, so it is not an
 * action; re-submitting a VALIDATION_FAILED draft goes through the same submit endpoint.
 */
export const VERSION_ACTIONS: Readonly<
  Record<ConfigurationVersionState, readonly ('edit' | 'preview' | 'submit' | 'approve' | 'schedule' | 'activate')[]>
> = {
  DRAFT: ['edit', 'preview', 'submit'],
  VALIDATION_FAILED: ['edit', 'preview', 'submit'],
  READY_FOR_REVIEW: ['preview', 'approve'],
  APPROVED: ['preview', 'schedule', 'activate'],
  SCHEDULED: ['preview', 'activate'],
  ACTIVE: [],
  SUPERSEDED: [],
};

export type VersionAction = (typeof VERSION_ACTIONS)[ConfigurationVersionState][number];

/** One institution configuration version, as the API returns it (dates as ISO strings). */
export interface ConfigurationVersion {
  id: string;
  institutionId: string;
  versionNumber: number;
  state: ConfigurationVersionState;
  institutionOverrides: ConfigurationValueMap;
  campusOverrides: Record<string, ConfigurationValueMap>;
  resolvedOperatingModel: Record<string, string> | null;
  provenance: Partial<Record<ConfigurationKey, ResolvedConfigurationValue>> | null;
  resolvedCampusModels: Record<string, Record<string, string>>;
  validationErrors: string[];
  sourceLayerIds: string[];
  changeReason: string | null;
  createdBy: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  effectiveFrom: string | null;
  activatedAt: string | null;
  supersededAt: string | null;
  supersededByVersionId: string | null;
  previousVersionId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * The onboarding record: what the institution IS. Its type, subsystem and
 * country select which shared layers apply — without this record the resolver
 * has nothing to resolve against, which is the state of every institution
 * created before the configuration work.
 */
export interface InstitutionConfigurationRecord {
  institutionId: string;
  institutionType: string;
  educationSubsystem: string;
  ownershipModel: string;
  countryCode: string;
  /** NOT_APPLICABLE | PENDING | SENT | FAILED — did EduCore receive the academic hand-off? */
  academicSeedStatus: string;
  academicSeedAt: string | null;
  academicSeedError: string | null;
}

/** POST /platform/institution-configurations — mirrors core's ConfigureInstitutionDto. */
export interface ConfigureInstitutionRequest {
  institutionId: string;
  institutionType: string;
  educationSubsystem: string;
  ownershipModel: string;
  countryCode: string;
}

export interface ConfigureInstitutionOutcome {
  created: boolean;
  changed: boolean;
  academicSeedAttempted: boolean;
  warnings: string[];
}

/** Mirrors of core's enums — the form's whole vocabulary, never free text. */
export const INSTITUTION_TYPES = [
  'PRIMARY_SCHOOL',
  'SECONDARY_SCHOOL',
  'COMBINED_SCHOOL',
  'UNIVERSITY',
  'HIGHER_EDUCATION_INSTITUTION',
  'TRAINING_INSTITUTION',
  'VOCATIONAL_INSTITUTION',
  'SCHOOL_GROUP',
  'EDUCATION_AUTHORITY',
] as const;

export const EDUCATION_SUBSYSTEMS = [
  'ANGLOPHONE',
  'FRANCOPHONE',
  'BILINGUAL',
  'NOT_APPLICABLE',
] as const;

export const OWNERSHIP_MODELS = ['PUBLIC', 'PRIVATE', 'CONFESSIONAL', 'COMMUNITY'] as const;

/** POST /:institutionId/versions and PATCH .../:versionId bodies. */
export interface ConfigurationOverridesRequest {
  institutionOverrides?: ConfigurationValueMap;
  campusOverrides?: Record<string, ConfigurationValueMap>;
  changeReason?: string;
}

/** PUT /layers/:scope/:scopeKey body. */
export interface UpsertConfigurationLayerRequest {
  label: string;
  values: ConfigurationValueMap;
  enabled?: boolean;
}

/** POST .../activate — the version put in force and the one it closed, if any. */
export interface ActivationResult {
  activated: ConfigurationVersion;
  superseded: ConfigurationVersion | null;
}
